// What a host adds to the People screen, given to `personsScreen({ link })`.
// Linking a person to an account needs to know who the other accounts are,
// which only cloud does (the members of the workspaces the owner is in); the
// offline edition gives nothing and the menu has no "Link to member…".
//
// Promise: with `link`, a person's "…" offers to link them to an account (or
// to unlink the one linked), and the person's page shows who that account is.
import { createContext, type ReactNode, useContext } from "react";

/** An account a person can be linked to, as the host knows it. */
export interface LinkAccount {
  userId: string;
  name: string;
  /** The picture's URL, if any. */
  image: string | null;
}

export interface PersonsExtensions {
  link?: {
    /**
     * The accounts the owner may link a person to (null while loading): the
     * server refuses others. A hook, called while the person is shown.
     */
    useAccounts(): readonly LinkAccount[] | null;
  };
}

const Context = createContext<PersonsExtensions>({});

export function PersonsExtensionsProvider({
  extensions,
  children,
}: {
  extensions: PersonsExtensions;
  children: ReactNode;
}) {
  return <Context.Provider value={extensions}>{children}</Context.Provider>;
}

export const usePersonsExtensions = (): PersonsExtensions =>
  useContext(Context);
